import {
  loadAdminDashboard,
  loadAdminOrganizationTeamsPage,
  loadAttendanceDashboardPage,
  loadAutomationCatalogPage,
  loadCampaignAdminPage,
  loadCampaignLaunchPage,
  loadCampaignParticipantSignupPage,
  loadCareCenterPage,
  loadChildrenCheckInPage,
  loadChildrenTeachersPage,
  loadChildrenCheckoutPage,
  loadChooseOrganizationForDonation,
  loadDesignCenterPage,
  loadDonatePage,
  loadDonationStatusPage,
  loadDonorsPage,
  loadFundingCenterPage,
  loadGalleryPage,
  loadGroupsPage,
  loadMediaVaultPage,
  loadMemberAttendancePage,
  loadMemberProfilePage,
  loadMyFollowUpsPage,
  loadMyGivingPage,
  loadMyTeamsPage,
  loadNotificationCenterPage,
  loadOrgBiblePage,
  loadOrgCheckInPage,
  loadOrgDashboard,
  loadOrgGroupsManagerPage,
  loadOrgMorePage,
  loadOrgPayoutSetupPage,
  loadOrgProfilePage,
  loadOrgPublicProfile,
  loadOrgVerificationPage,
  loadOrganizationTeamWorkspacePage,
  loadParticipantPage,
  loadPublicCampaignPage,
  loadPublicChurchRoutePage,
  loadPublicLeadershipPage,
  loadPublicMinistryPage,
  loadPublicOrgDashboardPage,
  loadPublicServiceContentLibrary,
  loadPublicServiceHub,
  loadReportsPage,
  loadTeamRolesPage,
  loadWebsiteVisitorsPage,
} from "../routes/pageLoaders";

const prefetchedKeys = new Set();
const RESERVED_TOP_LEVEL_ROUTES = new Set([
  "admin",
  "auth",
  "dashboard",
  "discover",
  "donate",
  "groups",
  "lightly",
  "login",
  "missions",
  "onboarding",
  "participant",
  "platform",
  "privacy",
  "register",
  "signup-org",
  "signup-participant",
  "test",
  "test-search",
  "verify-email",
]);

function prefetch(key, loader) {
  if (!key || typeof loader !== "function" || prefetchedKeys.has(key)) return;
  prefetchedKeys.add(key);
  loader().catch(() => {
    prefetchedKeys.delete(key);
  });
}

function prefetchMany(entries) {
  entries.forEach(([key, loader]) => prefetch(key, loader));
}

function cleanPathname(pathname = "") {
  const path = String(pathname || "/").split("#")[0].split("?")[0] || "/";
  return path.replace(/\/{2,}/g, "/").replace(/\/+$/, "") || "/";
}

function isLikelyPublicOrgRoot(path) {
  const clean = cleanPathname(path);
  const match = clean.match(/^\/(?:o\/)?([^/]+)$/i);
  if (!match) return false;
  return !RESERVED_TOP_LEVEL_ROUTES.has(String(match[1] || "").toLowerCase());
}

function isCampaignDonatePath(path) {
  return /\/campaigns\/[^/]+\/(?:donate|fundraisers\/[^/]+\/donate|f\/[^/]+\/donate)$/i.test(path);
}

function isFundraiserPath(path) {
  return /\/(?:campaigns\/[^/]+\/(?:fundraisers|f)|participant)\/[^/]+/i.test(path);
}

export function prefetchForPath(pathname = "") {
  const path = cleanPathname(pathname);

  if (path === "/donate") {
    prefetch("choose-org-donation", loadChooseOrganizationForDonation);
    return;
  }

  if (/(^|\/)(donate|org-donate)(\/|$)/i.test(path) || isCampaignDonatePath(path)) {
    prefetchMany([
      ["donate-page", loadDonatePage],
      ["donation-status", loadDonationStatusPage],
    ]);
    return;
  }

  if (/\/(?:success|cancel)$/i.test(path) || /\/give\/success$/i.test(path)) {
    prefetch("donation-status", loadDonationStatusPage);
    return;
  }

  if (/\/(?:campaigns|c)\/[^/]+\/launch$/i.test(path)) {
    prefetch("campaign-launch", loadCampaignLaunchPage);
    return;
  }

  if (/\/campaigns\/[^/]+\/edit$/i.test(path) || /\/admin\/[^/]+\/campaigns\/[^/]+\/edit$/i.test(path)) {
    prefetch("campaign-admin", loadCampaignAdminPage);
    return;
  }

  if (/\/campaigns\/[^/]+\/become-a-fundraiser$/i.test(path) || /\/f\/[^/]+\/edit$/i.test(path)) {
    prefetch("campaign-participant-signup", loadCampaignParticipantSignupPage);
    return;
  }

  if (isFundraiserPath(path)) {
    prefetchMany([
      ["participant-page", loadParticipantPage],
      ["donate-page", loadDonatePage],
    ]);
    return;
  }

  if (/\/(?:campaigns|c)\/[^/]+$/i.test(path)) {
    prefetchMany([
      ["public-campaign", loadPublicCampaignPage],
      ["donate-page", loadDonatePage],
    ]);
    return;
  }

  if (/\/(?:attendance)$/i.test(path)) {
    prefetchMany([
      ["attendance-dashboard", loadAttendanceDashboardPage],
      ["member-attendance", loadMemberAttendancePage],
      ["org-checkin", loadOrgCheckInPage],
    ]);
    return;
  }

  if (/\/(?:checkin|kiosk|churchservice\/checkin)$/i.test(path)) {
    prefetch("org-checkin", loadOrgCheckInPage);
    return;
  }

  if (/\/children-teachers$/i.test(path)) {
    prefetch("children-teachers", loadChildrenTeachersPage);
    return;
  }

  if (/\/children-checkin$/i.test(path)) {
    prefetch("children-checkin", loadChildrenCheckInPage);
    return;
  }

  if (/\/children-checkout$/i.test(path)) {
    prefetch("children-checkout", loadChildrenCheckoutPage);
    return;
  }

  if (/\/(?:admin|platform\/o\/[^/]+)\/[^/]*dashboard$/i.test(path) || /^\/admin(?:\/|$)/i.test(path)) {
    prefetchMany([
      ["org-dashboard", loadOrgDashboard],
      ["admin-dashboard", loadAdminDashboard],
      ["funding-center", loadFundingCenterPage],
      ["attendance-dashboard", loadAttendanceDashboardPage],
    ]);
    return;
  }

  if (/\/(?:design|admin\/design)$/i.test(path)) {
    prefetch("design-center", loadDesignCenterPage);
    return;
  }

  if (/\/(?:funding|admin\/funding)$/i.test(path)) {
    prefetch("funding-center", loadFundingCenterPage);
    return;
  }

  if (/\/(?:donors)$/i.test(path)) {
    prefetch("donors", loadDonorsPage);
    return;
  }

  if (/\/(?:reports)$/i.test(path)) {
    prefetch("reports", loadReportsPage);
    return;
  }

  if (/\/(?:visitors)$/i.test(path)) {
    prefetch("website-visitors", loadWebsiteVisitorsPage);
    return;
  }

  if (/\/(?:media-vault)$/i.test(path)) {
    prefetch("media-vault", loadMediaVaultPage);
    return;
  }

  if (/\/(?:teams)(?:\/|$)/i.test(path)) {
    prefetchMany([
      ["admin-teams", loadAdminOrganizationTeamsPage],
      ["my-teams", loadMyTeamsPage],
      ["team-workspace", loadOrganizationTeamWorkspacePage],
    ]);
    return;
  }

  if (/\/(?:notifications)$/i.test(path)) {
    prefetch("notifications", loadNotificationCenterPage);
    return;
  }

  if (/\/(?:automations)$/i.test(path)) {
    prefetch("automations", loadAutomationCatalogPage);
    return;
  }

  if (/\/(?:team|roles)$/i.test(path)) {
    prefetch("team-roles", loadTeamRolesPage);
    return;
  }

  if (/\/(?:profile)$/i.test(path)) {
    prefetch("org-profile", loadOrgProfilePage);
    return;
  }

  if (/\/(?:verify)$/i.test(path)) {
    prefetch("org-verification", loadOrgVerificationPage);
    return;
  }

  if (/\/(?:payouts)$/i.test(path)) {
    prefetch("org-payouts", loadOrgPayoutSetupPage);
    return;
  }

  if (/\/(?:care)$/i.test(path)) {
    prefetch("care-center", loadCareCenterPage);
    return;
  }

  if (/\/(?:follow-ups|assign-follow-ups)$/i.test(path)) {
    prefetch("follow-ups", loadMyFollowUpsPage);
    return;
  }

  if (/\/(?:me)$/i.test(path)) {
    prefetch("member-profile", loadMemberProfilePage);
    return;
  }

  if (/\/(?:my-giving)$/i.test(path)) {
    prefetch("my-giving", loadMyGivingPage);
    return;
  }

  if (/\/(?:ministries)(?:\/|$)/i.test(path)) {
    prefetch("public-ministry", loadPublicMinistryPage);
    return;
  }

  if (/\/(?:gallery)$/i.test(path)) {
    prefetch("gallery", loadGalleryPage);
    return;
  }

  if (/\/(?:leadership|about\/leadership)$/i.test(path)) {
    prefetch("leadership", loadPublicLeadershipPage);
    return;
  }

  if (/\/(?:messages|series)(?:\/|$)/i.test(path)) {
    prefetch("service-content", loadPublicServiceContentLibrary);
    return;
  }

  if (/\/(?:services)(?:\/|$)/i.test(path)) {
    prefetch("service-hub", loadPublicServiceHub);
    return;
  }

  if (/\/(?:more)$/i.test(path)) {
    prefetch("org-more", loadOrgMorePage);
    return;
  }

  if (/\/(?:bible)$/i.test(path)) {
    prefetch("org-bible", loadOrgBiblePage);
    return;
  }

  if (/\/(?:groups)(?:\/manage)?$/i.test(path)) {
    prefetchMany([
      ["groups-page", loadGroupsPage],
      ["org-groups-manager", loadOrgGroupsManagerPage],
    ]);
    return;
  }

  if (isLikelyPublicOrgRoot(path)) {
    prefetchMany([
      ["public-org-dashboard", loadPublicOrgDashboardPage],
      ["public-church-route", loadPublicChurchRoutePage],
      ["donate-page", loadDonatePage],
      ["public-org-profile", loadOrgPublicProfile],
    ]);
  }
}

export function prefetchLikelyRoutes(pathname = "") {
  const path = cleanPathname(pathname);

  if (isLikelyPublicOrgRoot(path)) {
    prefetchMany([
      ["donate-page", loadDonatePage],
      ["public-church-route", loadPublicChurchRoutePage],
      ["public-campaign", loadPublicCampaignPage],
    ]);
    return;
  }

  if (/\/platform\/o\/[^/]+\/dashboard$/i.test(path)) {
    prefetchMany([
      ["design-center", loadDesignCenterPage],
      ["attendance-dashboard", loadAttendanceDashboardPage],
      ["funding-center", loadFundingCenterPage],
      ["donors", loadDonorsPage],
    ]);
    return;
  }

  if (/(^|\/)donate(\/|$)/i.test(path)) {
    prefetch("donation-status", loadDonationStatusPage);
  }
}

function hrefToSameOriginPath(href) {
  if (!href || typeof window === "undefined") return "";
  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return "";
    if (url.hash && `${url.pathname}${url.search}` === `${window.location.pathname}${window.location.search}`) {
      return "";
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return "";
  }
}

function prefetchFromEvent(event) {
  const target = event.target;
  const anchor = target?.closest?.("a[href]");
  const pathname = hrefToSameOriginPath(anchor?.getAttribute?.("href"));
  if (pathname) prefetchForPath(pathname);
}

export function setupIntentRoutePrefetch(root = document) {
  if (!root?.addEventListener) return () => {};
  root.addEventListener("mouseover", prefetchFromEvent, { passive: true });
  root.addEventListener("focusin", prefetchFromEvent);
  root.addEventListener("touchstart", prefetchFromEvent, { passive: true });
  return () => {
    root.removeEventListener("mouseover", prefetchFromEvent);
    root.removeEventListener("focusin", prefetchFromEvent);
    root.removeEventListener("touchstart", prefetchFromEvent);
  };
}
